import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { assetsService } from '../services/assets.service';

export interface MediaAsset {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  cdnUrl: string;
  thumbnailUrl?: string;
  tags: string[];
  altText?: string;
  folder: string;
  uploadedBy: string;
  teamId: string;
  usageCount: number;
  usedIn: Array<{
    projectId?: string;
    contentId?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAssetData {
  file: File;
  tags?: string[];
  altText?: string;
  folder?: string;
}

export interface UpdateAssetData {
  tags?: string[];
  altText?: string;
  folder?: string;
}

interface AssetsState {
  // State
  assets: MediaAsset[];
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  filterCategory: string;
  filterFolder: string;
  selectedAssets: string[];
  viewingAsset: MediaAsset | null;

  // Actions
  setSearchTerm: (term: string) => void;
  setFilterCategory: (category: string) => void;
  setFilterFolder: (folder: string) => void;
  setViewingAsset: (asset: MediaAsset | null) => void;
  toggleAssetSelection: (id: string) => void;
  clearSelection: () => void;
  selectAll: () => void;

  // Async Actions
  fetchAssets: () => Promise<void>;
  uploadAsset: (data: CreateAssetData) => Promise<MediaAsset | null>;
  updateAsset: (id: string, data: UpdateAssetData) => Promise<MediaAsset | null>;
  deleteAsset: (id: string) => Promise<boolean>;
  deleteAssets: (ids: string[]) => Promise<boolean>;
  downloadAsset: (id: string) => Promise<void>;
  copyAssetLink: (id: string) => Promise<void>;
  duplicateAsset: (id: string) => Promise<MediaAsset | null>;
  moveAssets: (ids: string[], folder: string) => Promise<boolean>;
  tagAssets: (ids: string[], tags: string[]) => Promise<boolean>;
}

export const useAssetsStore = create<AssetsState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        assets: [],
        isLoading: false,
        error: null,
        searchTerm: '',
        filterCategory: 'all',
        filterFolder: 'all',
        selectedAssets: [],
        viewingAsset: null,

        // Filter actions
        setSearchTerm: (term) => set({ searchTerm: term }),
        setFilterCategory: (category) => set({ filterCategory: category }),
        setFilterFolder: (folder) => set({ filterFolder: folder }),
        setViewingAsset: (asset) => set({ viewingAsset: asset }),

        // Selection actions
        toggleAssetSelection: (id) => {
          set((state) => ({
            selectedAssets: state.selectedAssets.includes(id)
              ? state.selectedAssets.filter(assetId => assetId !== id)
              : [...state.selectedAssets, id]
          }));
        },
        clearSelection: () => set({ selectedAssets: [] }),
        selectAll: () => {
          const { assets } = get();
          set({ selectedAssets: assets.map(asset => asset.id) });
        },

        // Fetch assets
        fetchAssets: async () => {
          set({ isLoading: true, error: null });
          try {
            const assets = await assetsService.getAssets();
            set({
              assets,
              isLoading: false,
              error: null
            });
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch assets'
            });
          }
        },

        // Upload asset
        uploadAsset: async (data) => {
          set({ isLoading: true, error: null });
          try {
            const asset = await assetsService.uploadAsset(data);
            set((state) => ({
              assets: [asset, ...state.assets],
              isLoading: false,
              error: null
            }));
            return asset;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to upload asset'
            });
            return null;
          }
        },

        // Update asset
        updateAsset: async (id, data) => {
          set({ isLoading: true, error: null });
          try {
            const updatedAsset = await assetsService.updateAsset(id, data);
            set((state) => ({
              assets: state.assets.map(asset =>
                asset.id === id ? updatedAsset : asset
              ),
              isLoading: false,
              error: null
            }));
            return updatedAsset;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to update asset'
            });
            return null;
          }
        },

        // Delete asset
        deleteAsset: async (id) => {
          set({ isLoading: true, error: null });
          try {
            await assetsService.deleteAsset(id);
            set((state) => ({
              assets: state.assets.filter(asset => asset.id !== id),
              selectedAssets: state.selectedAssets.filter(assetId => assetId !== id),
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to delete asset'
            });
            return false;
          }
        },

        // Delete multiple assets
        deleteAssets: async (ids) => {
          set({ isLoading: true, error: null });
          try {
            await assetsService.deleteAssets(ids);
            set((state) => ({
              assets: state.assets.filter(asset => !ids.includes(asset.id)),
              selectedAssets: [],
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to delete assets'
            });
            return false;
          }
        },

        // Download asset
        downloadAsset: async (id) => {
          try {
            await assetsService.downloadAsset(id);
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to download asset'
            });
          }
        },

        // Copy asset link
        copyAssetLink: async (id) => {
          try {
            await assetsService.copyAssetLink(id);
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to copy link'
            });
          }
        },

        // Duplicate asset
        duplicateAsset: async (id) => {
          set({ isLoading: true, error: null });
          try {
            const duplicatedAsset = await assetsService.duplicateAsset(id);
            set((state) => ({
              assets: [duplicatedAsset, ...state.assets],
              isLoading: false,
              error: null
            }));
            return duplicatedAsset;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to duplicate asset'
            });
            return null;
          }
        },

        // Move assets
        moveAssets: async (ids, folder) => {
          set({ isLoading: true, error: null });
          try {
            await assetsService.moveAssets(ids, folder);
            set((state) => ({
              assets: state.assets.map(asset =>
                ids.includes(asset.id) ? { ...asset, folder } : asset
              ),
              selectedAssets: [],
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to move assets'
            });
            return false;
          }
        },

        // Tag assets
        tagAssets: async (ids, tags) => {
          set({ isLoading: true, error: null });
          try {
            await assetsService.tagAssets(ids, tags);
            set((state) => ({
              assets: state.assets.map(asset =>
                ids.includes(asset.id)
                  ? { ...asset, tags: [...new Set([...asset.tags, ...tags])] }
                  : asset
              ),
              selectedAssets: [],
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to tag assets'
            });
            return false;
          }
        },
      }),
      {
        name: 'assets-storage',
        partialize: (state) => ({
          assets: state.assets,
          searchTerm: state.searchTerm,
          filterCategory: state.filterCategory,
          filterFolder: state.filterFolder,
        }),
      }
    ),
    {
      name: 'assets-store',
    }
  )
);

